import {
  AlgoRerunDto,
  buildNow,
  BusinessUnit,
  DATE_FORMAT_ALT,
  Sample,
  Series,
} from '@cambridgeassessment/lib-ee-commons';
import { Entry } from '@cambridgeassessment/lib-ee-commons/src/domain/db/entries';
import { SampleSearchParams, SamplesManagementActionTypes } from './types';
import * as service from '../../service/samplesManagement/index';
import { NotificationActionTypes } from '../notification/types';
import { displayNotification } from '../notification/actions';
import * as actions from './actions';
import { downloadFromUrl } from '../../util/downloadHandler';

type Dispatch = (action: SamplesManagementActionTypes | NotificationActionTypes) => void;

export const findSample =
  (searchParams: SampleSearchParams) =>
  async (dispatch: Dispatch): Promise<void> => {
    try {
      dispatch(actions.setIsLoading(true));
      const sample = await service.findSample(searchParams);
      dispatch(actions.setSample(sample));
    } catch (err) {
      dispatch(actions.resetState());
      dispatch(actions.setError());
    } finally {
      dispatch(actions.setIsLoading(false));
    }
  };

export const getCentreLearners =
  (businessUnitId: BusinessUnit['id'], foundSampleId: string) =>
  async (dispatch: Dispatch): Promise<void> => {
    try {
      dispatch(actions.setIsEntriesLoading(true));
      const result = await service.getCentreLearners(businessUnitId, foundSampleId);

      dispatch(actions.setCentreLearners(result));
    } catch (err) {
      dispatch(actions.setError());
      dispatch(displayNotification('Failed to get entries data'));
    } finally {
      dispatch(actions.setIsEntriesLoading(false));
    }
  };

export const addEntryToSample =
  (businessUnitId: string, sampleId: string, newEntries: Entry[], field: keyof Sample) =>
  async (dispatch: Dispatch): Promise<void> => {
    try {
      dispatch(actions.setIsEntriesPatching(true));
      await service.updateSample(businessUnitId, sampleId, newEntries, field);
      dispatch(displayNotification('Successfully added new sample(s)'));
    } catch (error) {
      dispatch(actions.setError());
      dispatch(displayNotification('Failed to update sample'));
    } finally {
      dispatch(actions.setIsEntriesPatching(false));
    }
  };

export const getConsortiumData =
  (businessUnitId: string, seriesId: number, centreId: string, assessmentCode: string, componentNumber: string) =>
  async (dispatch: Dispatch): Promise<void> => {
    try {
      const res = await service.getConsortiaInfo(businessUnitId, seriesId, centreId, assessmentCode, componentNumber);
      dispatch(actions.setConsortiumData(res));
    } catch (error) {
      dispatch(actions.setError());
      dispatch(displayNotification('Failed to get consortium data'));
    }
  };

export const replaceLearner =
  (businessUnitId: string, learnerOut: Entry, learnerIn: Entry, sampleId: string) =>
  async (dispatch: Dispatch): Promise<void> => {
    try {
      dispatch(actions.setIsEntriesPatching(true));
      await service.replaceLearner(businessUnitId, learnerIn, learnerOut, sampleId);
      dispatch(displayNotification('The candidate has been replaced'));
    } catch (error) {
      dispatch(actions.setError());
      dispatch(displayNotification('Failed to replace candidate'));
    } finally {
      dispatch(actions.setIsEntriesPatching(false));
    }
  };

export const rerunSampleAlgo =
  (sampleId: string, rerunInfo: AlgoRerunDto, businessUnitId: string) =>
  async (dispatch: Dispatch): Promise<void> => {
    try {
      dispatch(actions.setIsSampleBeingRerun(true));
      const result = await service.rerunSampleAlgo(sampleId, businessUnitId, rerunInfo);
      dispatch(actions.setSample(result));
    } catch (error) {
      dispatch(actions.setError());
      dispatch(displayNotification('Failed to rerun sample'));
    } finally {
      dispatch(actions.setIsSampleBeingRerun(false));
    }
  };

export const getSampleAlgoHistory =
  (searchParams: SampleSearchParams) =>
  async (dispatch: Dispatch): Promise<void> => {
    try {
      dispatch(actions.setIsLoading(true));
      const res = await service.getSampleAlgoHistory(searchParams);
      dispatch(actions.setAlgoReruns(res));
    } catch (error) {
      dispatch(actions.setError());
      dispatch(displayNotification('Failed to fetch algo history'));
    } finally {
      dispatch(actions.setIsLoading(false));
    }
  };

export const downloadSampleReport =
  (businessUnitId: BusinessUnit['id'], seriesId: Series['id']) =>
  async (dispatch: Dispatch): Promise<void> => {
    try {
      const { downloadUrl } = await service.getSampleReportDownloadLink(businessUnitId, seriesId);
      const today = buildNow()().format(DATE_FORMAT_ALT);
      const fileName = `Sample selection amendment report - ${today}`;
      downloadFromUrl(downloadUrl, fileName);
    } catch (error) {
      dispatch(displayNotification('Failed to get download URL'));
    }
  };

export default {
  findSample,
  getCentreLearners,
  addEntryToSample,
  getConsortiumData,
  replaceLearner,
  rerunSampleAlgo,
  getSampleAlgoHistory,
  downloadSampleReport,
};
